import { inject } from '@angular/core';

import { Actions, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, catchError, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';

import { GuaranteeApplicationsService } from '@hm/core/services/guarantee-applications.service';
import { PrescriptionsStorageService } from '@hm/core/services/prescription-storage.service';
import { PrescriptionsService } from '@hm/core/services/prescriptions.service';
import { selectMemberProfile } from '@hm/core/stores/member-hub/member-hub.selectors';
import * as PrescriptionsActions from '@hm/core/stores/prescriptions/prescriptions.actions';
import { loadPrescriptionDraftsFromStorage } from '@hm/core/stores/prescriptions/prescriptions.actions';
import { selectPrescriptionDrafts, selectPrescriptionsFilters, selectPrescriptionsPage, selectPrescriptionsPageSize } from '@hm/core/stores/prescriptions/prescriptions.selectors';

import { AppointmentManagementModalComponent } from '@hm/ui/modals/appointment-management-modal/appointment-management-modal.component';
import { AppointmentsDetailsModalComponent } from '@hm/ui/modals/appointments-details-modal/appointments-details-modal.component';

import { GuaranteeApplicationSearchModel } from '@lib/core/models/guarantee-application.interface';
import { ModalActionTypes } from '@lib/core/models/modal-action-types.enum';
import {
  GenericServiceValue,
  PersonalDoctorValue,
  PharmacologicalTreatmentValue,
  Prescription,
  PrescriptionFormValue,
  PrescriptionPlanning,
} from '@lib/core/models/prescription.interface';
import { Visit } from '@lib/core/models/visit.interface';

export const fetchGuaranteesEffect = createEffect(
  (actions$ = inject(Actions), guaranteeApplicationsService = inject(GuaranteeApplicationsService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.fetchPrescriptions),
      switchMap(action =>
        guaranteeApplicationsService.searchGuaranteeApplications(action).pipe(
          map(pagedResponse => PrescriptionsActions.fetchPrescriptionsSuccess({ pagedResponse: pagedResponse })),
          catchError(() => of(PrescriptionsActions.fetchPrescriptionsFailure({ error: 'Failed to fetch guarantees' })))
        )
      )
    ),
  { functional: true }
);

export const openAppointmentsDetailsDialogEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.openAppointmentDetailsDialog),
      tap((action: { prescription: Prescription; appointments: Visit[] } & Action<'[Prescription Item] Open prescription details dialog'>) =>
        dialog.open(AppointmentsDetailsModalComponent, {
          data: {
            prescriptionData: action.prescription,
            plannings: action.prescription.planning || <PrescriptionPlanning[]>[],
            appointments: action.appointments,
          },
          maxHeight: '90vh',
          minWidth: '800px',
        })
      )
    ),
  { functional: true, dispatch: false }
);

export const openAppointmentCreationDialogEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.openAppointmentCreationDialog),
      tap((action: { action: Visit } & Action<'[Prescription Item] Open appointment creation dialog'>) =>
        dialog.open(AppointmentManagementModalComponent, {
          width: '800px',
          data: {
            actionType: ModalActionTypes.Create,
            action: action.action,
          },
        })
      )
    ),
  { functional: true, dispatch: false }
);

export const openAppointmentEditDialogEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.openAppointmentEditDialog),
      tap((action: { appointment: Visit } & Action<'[Prescription Item] Open appointment edit dialog'>) =>
        dialog.open(AppointmentManagementModalComponent, {
          width: '800px',
          data: {
            actionType: ModalActionTypes.Edit,
            appointment: action.appointment,
          },
        })
      )
    ),
  { functional: true, dispatch: false }
);

export const openAppointmentCancellationDialogEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.openAppointmentCancellationDialog),
      tap((action: { appointment: Visit } & Action<'[Prescription Item] Open appointment cancellation dialog'>) =>
        dialog.open(AppointmentManagementModalComponent, {
          width: '800px',
          data: {
            actionType: ModalActionTypes.Delete,
            appointment: action.appointment,
          },
        })
      )
    ),
  { functional: true, dispatch: false }
);

export const loadPrescriptionDraftsFromStorageEffect = createEffect(
  (actions$ = inject(Actions), prescriptionsStorageService = inject(PrescriptionsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => {
        const drafts = prescriptionsStorageService.loadDrafts();
        return loadPrescriptionDraftsFromStorage({ drafts });
      })
    ),
  { functional: true }
);

export const addPrescriptionDraftEffect = createEffect(
  (actions$ = inject(Actions), prescriptionsStorageService = inject(PrescriptionsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.addPrescriptionDraft),
      tap(
        (
          action: {
            draft: PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>;
          } & Action<'[Prescriptions Page] Add prescription draft'>
        ) => {
          const currentDrafts = prescriptionsStorageService.loadDrafts();
          const updatedDrafts = [...currentDrafts, action.draft];
          prescriptionsStorageService.saveDrafts(updatedDrafts);
        }
      )
    ),
  { functional: true, dispatch: false }
);

export const updatePrescriptionDraftEffect = createEffect(
  (actions$ = inject(Actions), prescriptionsStorageService = inject(PrescriptionsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.updatePrescriptionDraft),
      tap(
        (
          action: {
            draft: PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>;
          } & Action<'[Prescriptions Page] Update prescription draft'>
        ) => {
          const currentDrafts = prescriptionsStorageService.loadDrafts();
          const existingDraftIndex = currentDrafts.findIndex(existingDraft => existingDraft.id === action.draft.id);
          let updatedDrafts: PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>[];
          if (existingDraftIndex !== -1) {
            updatedDrafts = currentDrafts.map(existingDraft => (existingDraft.id === action.draft.id ? { ...existingDraft, ...action.draft } : existingDraft));
          } else {
            updatedDrafts = [...currentDrafts, action.draft];
          }
          prescriptionsStorageService.saveDrafts(updatedDrafts);
        }
      )
    ),
  { functional: true, dispatch: false }
);

export const deletePrescriptionDraftEffect = createEffect(
  (actions$ = inject(Actions), prescriptionsStorageService = inject(PrescriptionsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.deletePrescriptionDraft),
      tap((action: { id: string } & Action<'[Prescriptions Page] Delete prescription draft'>) => {
        const currentDrafts = prescriptionsStorageService.loadDrafts();
        const updatedDrafts = currentDrafts.filter(draft => draft.id !== action.id);
        prescriptionsStorageService.saveDrafts(updatedDrafts);
      })
    ),
  { functional: true, dispatch: false }
);

export const processPrescriptionsDraftsEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    prescriptionsService = inject(PrescriptionsService),
    prescriptionsStorageService = inject(PrescriptionsStorageService)
  ): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.processPrescriptionsDrafts),
      withLatestFrom(
        store.select(selectPrescriptionDrafts),
        store.select(selectPrescriptionsFilters),
        store.select(selectPrescriptionsPage),
        store.select(selectPrescriptionsPageSize),
        store.select(selectMemberProfile)
      ),
      switchMap(([action, drafts, filters, page, pageSize, memberProfile]) => {
        const draftsRequestBody = drafts
          .map(formValue => {
            if ('code' in formValue.metadata) {
              return prescriptionsService.createServiceProcessBody(<PrescriptionFormValue<GenericServiceValue>>formValue);
            }
            if ('doctorId' in formValue.metadata) {
              return prescriptionsService.createPersonalDoctorProcessBody(<PrescriptionFormValue<PersonalDoctorValue>>formValue);
            }
            return prescriptionsService.createMedicationProcessBody(<PrescriptionFormValue<PharmacologicalTreatmentValue>>formValue);
          })
          .filter(draft => !!draft.guaranteeId);
        const newDraftsRequestBody = draftsRequestBody.filter(draft => draft?.originalPrescriptionId === null);
        const updatedDraftsRequestBody = draftsRequestBody.filter(draft => !!draft?.originalPrescriptionId);
        if (newDraftsRequestBody?.length === 0 && updatedDraftsRequestBody?.length === 0) {
          of(PrescriptionsActions.processPrescriptionsDraftsFailure({ error: 'Drafts array provided for processing is empty' }));
        }
        return forkJoin([
          updatedDraftsRequestBody.length > 0 ? prescriptionsService.processPrescriptionsDraftsUpdate(action.memberId, updatedDraftsRequestBody) : of(null),
          newDraftsRequestBody.length > 0 ? prescriptionsService.processPrescriptionDraftsCreate(action.memberId, newDraftsRequestBody) : of(null),
        ]).pipe(
          switchMap(() => {
            prescriptionsStorageService.clearDrafts();

            const refetchPayload: Partial<GuaranteeApplicationSearchModel> = {
              prescriptionName: filters.search,
              page: 1,
              pageSize: page * pageSize,
              userId: memberProfile.userId || null,
              sortBy: 'lastUpdateDate',
              sortDirection: 'desc',
            };

            return [PrescriptionsActions.processPrescriptionsDraftsSuccess(), PrescriptionsActions.clearPrescriptions(), PrescriptionsActions.fetchPrescriptions(refetchPayload)];
          }),
          catchError(error => of(PrescriptionsActions.processPrescriptionsDraftsFailure({ error: error.message || 'Failed to process drafts' })))
        );
      })
    ),
  { functional: true }
);

export const resetPrescriptionsStateEffect = createEffect(
  (actions$ = inject(Actions), prescriptionsStorageService = inject(PrescriptionsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.resetPrescriptionsState),
      tap(() => {
        prescriptionsStorageService.clearDrafts();
      })
    ),
  { functional: true, dispatch: false }
);
